// src/features/legacy-reports/components/LegacyReportSections.tsx

import { useState } from 'react';
import {
  Paper,
  SegmentedControl,
  Skeleton,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { LineChart } from '@mantine/charts';
import { SortableTh } from '@/shared/components/SortableTh';
import { useTableSort, type TableSortColumn } from '@/shared/hooks/useTableSort';
import { LegacyReportsUtils } from '../LegacyReportsUtils';
import type {
  LegacyEmbedUrlReport,
  LegacyEmbedUrlTotals,
  LegacyGeoCityReport,
  LegacyGeoCountryReport,
  LegacyGeoRegionReport,
  LegacyTimeseriesReport,
  LegacyTotalsData,
} from '../types';

interface LegacyReportSectionsProps {
  timeseriesReport?: LegacyTimeseriesReport;
  geoCountryReport?: LegacyGeoCountryReport;
  geoRegionReport?: LegacyGeoRegionReport;
  geoCityReport?: LegacyGeoCityReport;
  embedUrlReport?: LegacyEmbedUrlReport;
  isLoading: boolean;
}

type GeoKind = 'country' | 'region' | 'city';

interface GeoRow {
  key: string;
  name: string;
  countryIso2: string;
  totals: LegacyTotalsData;
}

// Sort on raw comparable values (numbers/strings), never formatted display strings.
type GeoSortKey = 'location' | 'code' | 'views' | 'viewers' | 'watch';
const GEO_SORT: Record<GeoSortKey, TableSortColumn<GeoRow>> = {
  location: { accessor: (r) => r.name },
  code: { accessor: (r) => r.countryIso2 },
  views: { accessor: (r) => r.totals.cPlays, defaultDir: 'desc' },
  viewers: { accessor: (r) => r.totals.cUniques, defaultDir: 'desc' },
  watch: { accessor: (r) => r.totals.cDuration, defaultDir: 'desc' },
};

type EmbedSortKey = 'url' | 'views' | 'viewers';
const EMBED_SORT: Record<EmbedSortKey, TableSortColumn<LegacyEmbedUrlTotals>> = {
  url: { accessor: (r) => r.embedUrl },
  views: { accessor: (r) => r.totals.cPlays, defaultDir: 'desc' },
  viewers: { accessor: (r) => r.totals.cUniques, defaultDir: 'desc' },
};

// Legacy reports default to Views, descending, across every table.
const VIEWS_DESC = { key: 'views', dir: 'desc' } as const;

/**
 * Timeseries + geography + embed-URL sections shared by the account,
 * sub-accounts, and media-detail views (all carry the same nested shapes).
 */
export function LegacyReportSections({
  timeseriesReport,
  geoCountryReport,
  geoRegionReport,
  geoCityReport,
  embedUrlReport,
  isLoading,
}: LegacyReportSectionsProps) {
  const [geoKind, setGeoKind] = useState<GeoKind>('country');

  const chartData = (timeseriesReport?.items ?? []).map((item) => ({
    ts: LegacyReportsUtils.timeseriesLabel(item.ts, timeseriesReport!.unitOfTime),
    Views: item.totals.cPlays,
    Viewers: item.totals.cUniques,
  }));

  const geoChoices: { value: GeoKind; label: string }[] = [];
  if (geoCountryReport) geoChoices.push({ value: 'country', label: 'Country' });
  if (geoRegionReport) geoChoices.push({ value: 'region', label: 'Region' });
  if (geoCityReport) geoChoices.push({ value: 'city', label: 'City' });
  const activeGeoKind = geoChoices.some((c) => c.value === geoKind)
    ? geoKind
    : geoChoices[0]?.value;

  let geo: GeoRow[] = [];
  if (activeGeoKind === 'country') {
    geo = (geoCountryReport?.countryTotals ?? []).map((row) => ({
      key: `${row.countryIso2}-${row.country}`,
      name: row.country,
      countryIso2: row.countryIso2,
      totals: row.totals,
    }));
  } else if (activeGeoKind === 'region') {
    geo = (geoRegionReport?.regionTotals ?? []).map((row) => ({
      key: `${row.countryIso2}-${row.region}`,
      name: row.region,
      countryIso2: row.countryIso2,
      totals: row.totals,
    }));
  } else if (activeGeoKind === 'city') {
    geo = (geoCityReport?.cityTotals ?? []).map((row) => ({
      key: `${row.countryIso2}-${row.city}`,
      name: row.city,
      countryIso2: row.countryIso2,
      totals: row.totals,
    }));
  }

  const embedRows = embedUrlReport?.embedUrlTotals ?? [];

  const geoSort = useTableSort(geo, GEO_SORT, VIEWS_DESC);
  const embedSort = useTableSort(embedRows, EMBED_SORT, VIEWS_DESC);

  return (
    <>
      {timeseriesReport !== undefined && (
        <Paper withBorder p="md">
          <Title order={5} mb="xs">
            Views and viewers over time
          </Title>
          {isLoading ? (
            <Skeleton height={220} />
          ) : chartData.length === 0 ? (
            <Text c="dimmed" size="sm">
              No activity in this range.
            </Text>
          ) : (
            <LineChart
              h={220}
              data={chartData}
              dataKey="ts"
              series={[
                { name: 'Views', color: 'blue.6' },
                { name: 'Viewers', color: 'teal.5' },
              ]}
              curveType="monotone"
              withDots={false}
              withLegend
            />
          )}
        </Paper>
      )}

      {geoChoices.length > 0 && (
        <Paper withBorder p="md">
          <Title order={5} mb="xs">
            Geography
          </Title>
          {geoChoices.length > 1 && (
            <SegmentedControl
              size="xs"
              data={geoChoices}
              value={activeGeoKind}
              onChange={(value) => setGeoKind(value as GeoKind)}
              mb="sm"
            />
          )}
          {isLoading ? (
            <Skeleton height={160} />
          ) : geo.length === 0 ? (
            <Text c="dimmed" size="sm">
              No geographic data in this range.
            </Text>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <SortableTh columnKey="location" sortKey={geoSort.sortKey} sortDir={geoSort.sortDir} onSort={geoSort.onSort}>
                    Location
                  </SortableTh>
                  <SortableTh columnKey="code" sortKey={geoSort.sortKey} sortDir={geoSort.sortDir} onSort={geoSort.onSort} w={90}>
                    Code
                  </SortableTh>
                  <SortableTh columnKey="views" sortKey={geoSort.sortKey} sortDir={geoSort.sortDir} onSort={geoSort.onSort} ta="right">
                    Views
                  </SortableTh>
                  <SortableTh columnKey="viewers" sortKey={geoSort.sortKey} sortDir={geoSort.sortDir} onSort={geoSort.onSort} ta="right">
                    Viewers
                  </SortableTh>
                  <SortableTh columnKey="watch" sortKey={geoSort.sortKey} sortDir={geoSort.sortDir} onSort={geoSort.onSort} ta="right">
                    Watch time
                  </SortableTh>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {geoSort.sorted.map((row) => (
                  <Table.Tr key={row.key}>
                    <Table.Td>{row.name}</Table.Td>
                    <Table.Td>{row.countryIso2}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {LegacyReportsUtils.formatCount(row.totals.cPlays)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {LegacyReportsUtils.formatCount(row.totals.cUniques)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {LegacyReportsUtils.formatWatchSeconds(row.totals.cDuration)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      )}

      {embedUrlReport !== undefined && (
        <Paper withBorder p="md">
          <Title order={5} mb="xs">
            Embed URLs
          </Title>
          {isLoading ? (
            <Skeleton height={120} />
          ) : embedRows.length === 0 ? (
            <Text c="dimmed" size="sm">
              No embed activity in this range.
            </Text>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <SortableTh columnKey="url" sortKey={embedSort.sortKey} sortDir={embedSort.sortDir} onSort={embedSort.onSort}>
                    Embed URL
                  </SortableTh>
                  <SortableTh columnKey="views" sortKey={embedSort.sortKey} sortDir={embedSort.sortDir} onSort={embedSort.onSort} ta="right">
                    Views
                  </SortableTh>
                  <SortableTh columnKey="viewers" sortKey={embedSort.sortKey} sortDir={embedSort.sortDir} onSort={embedSort.onSort} ta="right">
                    Viewers
                  </SortableTh>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {embedSort.sorted.map((row) => (
                  <Table.Tr key={row.embedUrl}>
                    <Table.Td style={{ wordBreak: 'break-all' }}>{row.embedUrl}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {LegacyReportsUtils.formatCount(row.totals.cPlays)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {LegacyReportsUtils.formatCount(row.totals.cUniques)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      )}
    </>
  );
}
